"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { type PropsWithChildren, type ReactNode } from "react"
import { ChatSessionsSidebar } from "@/components/chat/ChatSessionsSidebar"
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable"
import {
  DEFAULT_AGENT_NAME,
  type AgentName,
} from "@/lib/agentcore-client"

type ChatLayoutProps = PropsWithChildren<{
  /** Callback invoked when the user clicks the "New Chat" header button. */
  onNewChat: () => void
  /** Controls whether the "New Chat" button is enabled. */
  canStartNewChat: boolean
  /** The currently active session ID (passed to the sessions sidebar). */
  activeSessionId: string
  /** Called when the user selects a session from the sidebar. */
  onSelectSession: (sessionId: string) => void
  /** Called when the user deletes a session from the sidebar. */
  onDeleteSession: (sessionId: string) => Promise<void>
  /** Called when the active session disappears from the list on refresh. */
  onActiveSessionGone?: () => void
  /** Incremented to trigger a refresh of the sessions list in the sidebar. */
  sessionRefreshTrigger: number
  /** Name of the agent whose sessions should be shown. */
  agent?: AgentName
  /** Optional right-side panel rendered in a fixed (non-resizable) area */
  rightPanel?: ReactNode
  resizablePanel?: ReactNode
  showResizablePanel?: boolean
}>

/**
 * Chat-specific layout with a sessions sidebar on the left and optional right panels.
 *
 * Supports two right-panel modes:
 * - `rightPanel`: fixed-width panel (e.g. ExtractionsSidebar) appended after the chat area.
 * - `resizablePanel` + `showResizablePanel`: when both are set, the chat and panel are
 *   placed inside a ResizablePanelGroup so the user can drag the divider.
 *
 * @param children              - Chat page content (i.e. `<ChatInterface />`).
 * @param onNewChat             - Handler called when the user starts a new chat.
 * @param canStartNewChat       - Whether the "New Chat" button should be enabled.
 * @param activeSessionId       - Current session ID for sidebar highlighting.
 * @param onSelectSession       - Handler for session selection from sidebar.
 * @param onDeleteSession       - Handler for session deletion from sidebar.
 * @param onActiveSessionGone   - Called when the active session disappears from the list.
 * @param sessionRefreshTrigger - Trigger value to refresh the sessions list.
 * @param agent                 - Name of the agent whose sessions to display.
 * @param rightPanel            - Optional fixed-width panel appended to the right.
 * @param resizablePanel        - Optional content rendered in the resizable right panel.
 * @param showResizablePanel    - Whether to activate the resizable split layout.
 * @returns The rendered chat layout element.
 */
export function ChatLayout({
  children,
  onNewChat,
  canStartNewChat,
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  onActiveSessionGone,
  sessionRefreshTrigger,
  agent = DEFAULT_AGENT_NAME,
  rightPanel,
  resizablePanel,
  showResizablePanel = false,
}: ChatLayoutProps): React.JSX.Element {
  return (
    <div className="flex h-[calc(100vh-48px)] overflow-hidden">
      <ChatSessionsSidebar
        activeSessionId={activeSessionId}
        onSelectSession={onSelectSession}
        onDeleteSession={onDeleteSession}
        onActiveSessionGone={onActiveSessionGone}
        refreshTrigger={sessionRefreshTrigger}
        agent={agent}
        onNewChat={onNewChat}
        canStartNewChat={canStartNewChat}
      />

      {resizablePanel ? (
        <ResizablePanelGroup direction="horizontal" className="flex-1 min-w-0">
          <ResizablePanel defaultSize={40} minSize={30}>
            <div className="h-full overflow-hidden">{children}</div>
          </ResizablePanel>
          {showResizablePanel && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={60} minSize={20}>
                <div className="h-full overflow-hidden">{resizablePanel}</div>
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      ) : (
        <>
          <div className="flex-1 min-w-0 overflow-hidden">{children}</div>
          {rightPanel}
        </>
      )}
    </div>
  )
}
